import Swiper from "swiper";
import { debounce } from "../tools/utils.js";

const MOBILE_QUERY = "(max-width: 879px)";
const FILTERS_GUTTER_REM = 2.5;
const MIN_FILTER_FONT_PX = 15;
const MIN_FILTER_PADDING_INLINE_PX = 10;

/**
 * Filtre client + slider mobile des membres d’équipe.
 */
export const TeamFilter = (() => {
	const syncSliderLayout = (sliderRoot, section) => {
		const rect = section.getBoundingClientRect();
		const offsetStart = Math.max(0, rect.left);
		const offsetEnd = Math.max(0, window.innerWidth - rect.right);

		sliderRoot.style.setProperty("--team-slider-inset-start", `${offsetStart}px`);
		sliderRoot.style.setProperty("--team-slider-inset-end", `${offsetEnd}px`);

		return { offsetStart, offsetEnd };
	};

	const initSlider = (section) => {
		const sliderRoot = section.querySelector(".team__slider");
		if (!sliderRoot) {
			return { refresh: () => {} };
		}

		const mobileBp = window.matchMedia(MOBILE_QUERY);
		let teamSwiper;

		const enableSwiper = () => {
			const { offsetStart, offsetEnd } = syncSliderLayout(sliderRoot, section);

			if (teamSwiper) {
				teamSwiper.params.slidesOffsetBefore = offsetStart;
				teamSwiper.params.slidesOffsetAfter = offsetEnd;
				teamSwiper.update();
				return;
			}

			teamSwiper = new Swiper(sliderRoot, {
				slidesPerView: 1.2,
				spaceBetween: 16,
				speed: 500,
				slideClass: "swiper-mobile-slide",
				wrapperClass: "swiper-mobile-wrapper",
				slidesOffsetBefore: offsetStart,
				slidesOffsetAfter: offsetEnd,
				watchOverflow: true,
			});
		};

		const disableSwiper = () => {
			if (!teamSwiper) {
				return;
			}

			teamSwiper.destroy(true, true);
			teamSwiper = undefined;
		};

		const breakpointChecker = () => {
			if (mobileBp.matches) {
				enableSwiper();
			} else {
				disableSwiper();
			}
		};

		const handleResize = debounce(() => {
			if (!mobileBp.matches) {
				return;
			}

			enableSwiper();
		}, 150);

		mobileBp.addEventListener("change", breakpointChecker);
		window.addEventListener("resize", handleResize);
		breakpointChecker();

		return {
			refresh: () => {
				if (!mobileBp.matches) {
					return;
				}

				enableSwiper();
				teamSwiper?.slideTo(0, 0);
			},
		};
	};

	const initFilter = (section, slider) => {
		const filtersWrap = section.querySelector(".team__filters-wrap");
		const filtersToggle = section.querySelector(".team__filters-toggle");
		const filtersToggleLabel = section.querySelector(".team__filters-toggle-label");
		const filtersNav = section.querySelector(".team__filters");
		const filters = section.querySelectorAll("[data-team-filter]");
		const cards = section.querySelectorAll("[data-team-category]");
		const mobileBp = window.matchMedia(MOBILE_QUERY);
		const placeholder = filtersToggleLabel?.dataset.placeholder || "";

		if (!filters.length || !cards.length) {
			return;
		}

		const isDropdownOpen = () => filtersWrap?.classList.contains("is-open") ?? false;
		const isDropdownLayout = () => mobileBp.matches || (filtersWrap?.classList.contains("is-dropdown") ?? false);

		const setDropdownOpen = (open) => {
			if (!filtersWrap || !filtersToggle) {
				return;
			}

			filtersWrap.classList.toggle("is-open", open);
			filtersToggle.setAttribute("aria-expanded", open ? "true" : "false");
		};

		const closeDropdown = () => setDropdownOpen(false);

		/**
		 * Largeur naturelle de la barre de filtres (mesurée hors mode « select »,
		 * le bouton « Tous » étant masqué dans ce mode d’affichage).
		 */
		const measureFiltersRowWidth = () => {
			const navStyles = getComputedStyle(filtersNav);
			const wrapStyles = getComputedStyle(filtersWrap);
			const chrome =
				parseFloat(navStyles.paddingInlineStart) +
				parseFloat(navStyles.paddingInlineEnd) +
				parseFloat(wrapStyles.borderInlineStartWidth) +
				parseFloat(wrapStyles.borderInlineEndWidth);

			return [...filters].reduce((total, filter) => total + filter.getBoundingClientRect().width, chrome);
		};

		/**
		 * La barre, centrée, peut dépasser la colonne de contenu (plafonnée par la
		 * ligne du page builder) : ce qui compte est qu’elle garde une marge de
		 * FILTERS_GUTTER_REM par rapport aux bords de la fenêtre.
		 */
		const measureAvailableWidth = () => {
			const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
			const viewportRoom = document.documentElement.clientWidth - FILTERS_GUTTER_REM * 2 * rootFontSize;

			return Math.max(section.clientWidth, viewportRoom);
		};

		const clearBarMetrics = () => {
			filtersWrap.style.removeProperty("--_filter-font-size");
			filtersWrap.style.removeProperty("--_filter-padding-inline");
		};

		const getDefaultBarMetrics = () => {
			const sample = filtersWrap.querySelector(".team__filter:not(.team__filter--all)") || filters[0];
			clearBarMetrics();

			const styles = getComputedStyle(sample);

			return {
				fontPx: parseFloat(styles.fontSize),
				paddingInlinePx: parseFloat(styles.paddingInlineStart),
			};
		};

		const applyBarMetrics = ({ fontPx, paddingInlinePx }, defaults) => {
			if (fontPx >= defaults.fontPx - 0.5) {
				filtersWrap.style.removeProperty("--_filter-font-size");
			} else {
				filtersWrap.style.setProperty("--_filter-font-size", `${fontPx}px`);
			}

			if (paddingInlinePx >= defaults.paddingInlinePx - 0.5) {
				filtersWrap.style.removeProperty("--_filter-padding-inline");
			} else {
				filtersWrap.style.setProperty("--_filter-padding-inline", `${paddingInlinePx}px`);
			}
		};

		const syncFiltersLayout = () => {
			if (!filtersWrap || !filtersNav) {
				return;
			}

			if (mobileBp.matches) {
				filtersWrap.classList.remove("is-dropdown");
				clearBarMetrics();
				return;
			}

			const wasDropdown = filtersWrap.classList.contains("is-dropdown");
			const available = measureAvailableWidth();

			filtersWrap.classList.remove("is-dropdown");
			clearBarMetrics();

			const defaults = getDefaultBarMetrics();
			let fit = null;

			outer: for (let fontPx = Math.floor(defaults.fontPx); fontPx >= MIN_FILTER_FONT_PX; fontPx -= 1) {
				for (
					let paddingInlinePx = Math.floor(defaults.paddingInlinePx);
					paddingInlinePx >= MIN_FILTER_PADDING_INLINE_PX;
					paddingInlinePx -= 1
				) {
					applyBarMetrics({ fontPx, paddingInlinePx }, defaults);

					if (measureFiltersRowWidth() <= available) {
						fit = { fontPx, paddingInlinePx };
						break outer;
					}
				}
			}

			if (fit === null) {
				filtersWrap.classList.add("is-dropdown");
				clearBarMetrics();
			} else {
				applyBarMetrics(fit, defaults);
			}

			if (wasDropdown !== filtersWrap.classList.contains("is-dropdown")) {
				closeDropdown();
			}
		};

		const updateToggleLabel = (button = null) => {
			if (!filtersToggleLabel) {
				return;
			}

			filtersToggleLabel.textContent = button ? button.textContent.trim() : placeholder;
		};

		const applyFilter = (slug, { animate = true } = {}) => {
			const visibleCards = [];
			const showAll = !slug;

			cards.forEach((card) => {
				const slide = card.closest(".team__slide");
				const categories = (card.dataset.teamCategory || "").trim().split(/\s+/).filter(Boolean);
				const isVisible = showAll || categories.includes(slug);

				if (!isVisible) {
					card.classList.remove("team-card--reveal");
					card.classList.add("team-card--hidden");
					card.classList.remove("team-card--featured");
					card.setAttribute("aria-hidden", "true");
					slide?.classList.add("team__slide--hidden");
					return;
				}

				visibleCards.push(card);
			});

			visibleCards.forEach((card, index) => {
				const slide = card.closest(".team__slide");

				card.classList.remove("team-card--hidden");
				card.setAttribute("aria-hidden", "false");
				slide?.classList.remove("team__slide--hidden");
				card.classList.toggle("team-card--featured", index === 0);

				if (animate) {
					card.classList.remove("team-card--reveal");
					card.style.setProperty("--reveal-delay", `${index * 80}ms`);
				}
			});

			const revealCards = () => {
				visibleCards.forEach((card) => {
					card.classList.add("team-card--reveal");
				});
				slider.refresh();
			};

			if (animate && visibleCards.length) {
				requestAnimationFrame(() => {
					requestAnimationFrame(revealCards);
				});
			} else {
				revealCards();
			}
		};

		const setActiveFilter = (button) => {
			filters.forEach((filter) => {
				const isActive = button !== null && filter === button;
				filter.classList.toggle("is-active", isActive);
				filter.setAttribute("aria-selected", isActive ? "true" : "false");
			});

			updateToggleLabel(button);
		};

		filtersToggle?.addEventListener("click", () => {
			setDropdownOpen(!isDropdownOpen());
		});

		document.addEventListener("click", (event) => {
			if (!filtersWrap || !isDropdownOpen()) {
				return;
			}

			if (!filtersWrap.contains(event.target)) {
				closeDropdown();
			}
		});

		document.addEventListener("keydown", (event) => {
			if (event.key === "Escape" && isDropdownOpen()) {
				closeDropdown();
				filtersToggle?.focus();
			}
		});

		mobileBp.addEventListener("change", () => {
			closeDropdown();
			syncFiltersLayout();
		});

		const handleFiltersResize = debounce(syncFiltersLayout, 150);
		window.addEventListener("resize", handleFiltersResize);

		if (window.ResizeObserver) {
			let lastWidth = 0;

			// Le changement de mode fait varier la hauteur de la section : on ne
			// remesure que si la largeur disponible a réellement bougé.
			new ResizeObserver(() => {
				if (section.clientWidth === lastWidth) {
					return;
				}

				lastWidth = section.clientWidth;
				handleFiltersResize();
			}).observe(section);
		}

		syncFiltersLayout();
		// Les webfonts changent la largeur des libellés : on remesure une fois chargées.
		document.fonts?.ready?.then(syncFiltersLayout);

		const allFilter = section.querySelector(".team__filter--all");

		filters.forEach((button) => {
			button.addEventListener("click", () => {
				const slug = button.dataset.teamFilter || "";

				// Barre horizontale : re-cliquer un pôle actif désélectionne (retour à tous)
				if (!isDropdownLayout() && button.classList.contains("is-active") && slug) {
					setActiveFilter(allFilter);
					applyFilter("");
					return;
				}

				setActiveFilter(button);
				applyFilter(slug);

				if (isDropdownLayout()) {
					closeDropdown();
				}
			});
		});

		setActiveFilter(allFilter);
		applyFilter("", { animate: false });
	};

	const initSection = (section) => {
		const slider = initSlider(section);

		if (section.classList.contains("js-team")) {
			initFilter(section, slider);
		}
	};

	const init = () => {
		document.querySelectorAll(".team").forEach(initSection);
	};

	return {
		init,
	};
})();
